(() => {
  /* ───────────────────────────────── Constants */
  const CLS_SELECTED   = 'is-selected';
  const DEF_COLOR      = '#6366F1';
  const ANIM_MS        = 150;
  const API            = {
    columns:   '/api/directions/',
    move:      '/api/phones/move/',
    moveBulk:  '/api/phones/move_bulk/',
    refresh:   '/api/refresh_numbers/',
    callsLimit:'/api/phones/update_calls_limit/',
    rotationDate:'/api/phones/update_rotation_date/'
  };

  /* ──────────────────────────────── Helpers */
  const csrf = () =>
    document.querySelector('[name="csrfmiddlewaretoken"]')?.value ||
    document.getElementById('add-column-btn')?.dataset.csrfToken || '';

  const fetchJSON = (url, opts = {}) =>
    fetch(url, {
      headers: { 'Content-Type': 'application/json', 'X-CSRFToken': csrf() },
      ...opts
    }).then(r => (r.ok ? r.json() : Promise.reject(r)));

  const postJSON  = (url, body) => fetchJSON(url, { method: 'POST', body: JSON.stringify(body) });
  const patchJSON = (url, body) => fetchJSON(url, { method: 'PATCH', body: JSON.stringify(body) });

  /* Add custom CSS for counterclockwise spin animation */
  const addCounterclockwiseStyles = () => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes spin-counterclockwise {
        from { transform: rotate(0deg); }
        to { transform: rotate(-360deg); }
      }
      .animate-spin-counterclockwise {
        animation: spin-counterclockwise 1s linear infinite;
      }
    `;
    document.head.appendChild(style);
  };

  /* ──────────────────────────────── Kanban class */
  class Kanban {
    constructor() {
      /* root elements */
      this.board      = document.getElementById('kanban-board');
      this.overlay    = document.getElementById('add-column-overlay');
      this.addBtn     = document.getElementById('add-column-btn');
      this.cancelBtn  = document.getElementById('cancel-add-column');
      this.saveBtn    = document.getElementById('create-column');
      this.titleInput = document.getElementById('column-title');
      this.systemSelect = document.getElementById('column-system');
      this.modalTitle = document.getElementById('modal-title');

      /* state */
      this.editingColumn = null;
      this.selectedColor = DEF_COLOR;
      this.pickr         = null;

      /* external plugins */
      if (window.Sortable && window.MultiDrag) Sortable.mount(new MultiDrag());

      /* bootstrap */
      addCounterclockwiseStyles(); // Add custom animation styles
      this.bindUI();
      this.initPickr();
      this.initExistingSortables();
      this.initGlobalDelegates();
      this.initCallsLimitModal();
      this.initBulkCallsLimitHandlers();
      this.initSelectHandlers();
      this.initRotationDateModal();
      this.initBulkRotationDateHandlers();
    }

    /* ───────────────────────── Pickr */
    initPickr() {
      this.pickr = Pickr.create({
        el: '#color-picker', theme: 'classic', default: this.selectedColor,
        swatches: [
          '#6366F1', '#FBBF24', '#34D399', '#F87171', '#60A5FA',
          '#F472B6', '#F97316', '#A78BFA', '#10B981', '#EF4444'
        ],
        components: { preview: true, hue: true, interaction: { input:true, save:true, clear:true } }
      });
      this.pickr.on('save', c => { this.selectedColor = c.toHEXA().toString(); this.toggleSaveBtn(); this.pickr.hide(); });
      this.pickr.on('clear',     () => { this.selectedColor = null;             this.toggleSaveBtn(); });
    }

    /* ───────────────────────── UI Bindings */
    bindUI() {
      this.addBtn   ?.addEventListener('click', () => this.openCreateModal());
      this.cancelBtn?.addEventListener('click', () => this.closeModal());
      this.overlay  ?.addEventListener('click', e => e.target === this.overlay && this.closeModal());
      this.titleInput?.addEventListener('input', () => this.toggleSaveBtn());
      this.saveBtn  ?.addEventListener('click', () => this.handleSave());
    }

    /* ───────────────────────── Sortable */
    initExistingSortables() {
      document.querySelectorAll('.phone-list').forEach(list => this.initSortable(list));
      setTimeout(() => {
        document.querySelectorAll('.phone-list').forEach(list => {
          const s = Sortable.get(list);
          s?.option('animation', ANIM_MS);
        });
      }, 500);
    }

    initSortable(list) {
      // Уничтожаем старый инстанс, если он есть
      const old = Sortable.get(list);
      if (old) old.destroy();

      // Создаем новый
      const sortable = new Sortable(list, {
        group: 'kanban',
        animation: ANIM_MS,
        easing: 'cubic-bezier(1,0,0,1)',
        multiDrag: true,
        selectedClass: CLS_SELECTED,
        ghostClass: 'opacity-50',
        fallbackOnBody: true,
        swapThreshold: 0.65,
        setData: this.createDragImage,
        onStart: e => this.onDragStart(e),
        onEnd: e => this.onDragEnd(e)
      });

      // Восстановление анимации multidrag:
      list.querySelectorAll('.phone-item').forEach(item => {
        Sortable.utils.deselect(item);
      });

      requestAnimationFrame(() => {
        list.querySelectorAll('.phone-checkbox:checked').forEach(cb => {
          const item = cb.closest('.phone-item');
          if (item) Sortable.utils.select(item);
        });
      });
    }

    /* ───────── drag helpers */
    createDragImage(dt) {
      const count = document.querySelectorAll('.' + CLS_SELECTED).length;
      if (count < 2) return;
      const ghost = Object.assign(document.createElement('div'), {
        textContent: `${count} элементов выбрано`,
        style: 'background:#fff;border-radius:4px;padding:6px 12px;font-weight:700;box-shadow:0 2px 5px rgba(0,0,0,.2);pointer-events:none;'
      });
      dt.setDragImage(ghost, 10, 10);
    }

    onDragStart(evt) {
      /* включаем анимацию во всех списках */
      document.querySelectorAll('.phone-list').forEach(list => {
        const s = Sortable.get(list);
        if (s) s.option('animation', ANIM_MS);
      });

      const from = evt.from;
      const checked = from.querySelectorAll('.phone-checkbox:checked');
      checked.forEach(cb => {
        const item = cb.closest('.phone-item');
        if (item && !item.classList.contains(CLS_SELECTED)) {
          Sortable.utils.select(item);
        }
      });
    }

    onDragEnd(evt) {
      const moved    = evt.items?.length ? evt.items : [evt.item];
      const targetCol= evt.to.closest('.kanban-column');
      const sourceCol= evt.from.closest('.kanban-column');
      if (moved.length > 1) {
        this.moveBulk(moved, targetCol, sourceCol);
      } else {
        this.moveSingle(moved[0], targetCol);
      }
    }

    moveSingle(item, targetCol) {
      if (!item || !targetCol) return;
      const number = item.dataset.phoneNumber;
      const direction_id = targetCol.dataset.directionId;
      postJSON(API.move, { number, direction_id }).catch(console.error);
    }

    moveBulk(items, targetCol, sourceCol) {
      const numbers = items.map(el => el.dataset.phoneNumber);
      const direction_id = targetCol.dataset.directionId;
      postJSON(API.moveBulk, { numbers, direction_id })
        .then(() => {
          /* keep checkboxes + ui states */
          items.forEach(el => el.querySelector('.phone-checkbox')?.setAttribute('checked', ''));
          [targetCol, sourceCol].forEach(col => {
            this.updateSelectAllState(col);
            this.updateBulkVisibility(col);
          });
        })
        .catch(console.error);
    }

    /* ───────────────────────── Column buttons delegation */
    initGlobalDelegates() {
      document.addEventListener('click', e => {
        const btn = e.target.closest('[data-action]');
        if (!btn) return;
        const col = btn.closest('.kanban-column');
        switch (btn.dataset.action) {
          case 'delete': this.deleteColumn(col);                 break;
          case 'edit':   this.openEditModal(col);                break;
          case 'refresh-numbers': this.refreshNumbers(btn, col); break;
          case 'edit-calls-limit': this.showCallsLimitModal(e);   break;
          case 'edit-rotation-date': this.showRotationDateModal(e); break;
        }
      });
    }

    async deleteColumn(col) {
      if (!col || !confirm('Удалить направление?')) return;
      try {
        await fetch(API.columns + col.dataset.directionId + '/', { method:'DELETE', headers:{'X-CSRFToken': csrf()} });
        col.remove();
        document.querySelectorAll('.phone-list').forEach(list => {
          this.initSortable(list);
        });
      } catch (err) { console.error('delete fail', err); }
    }

    /* ───────────────────────── Numbers refresh */
    async refreshNumbers(btn, col) {
      if (!btn || !col) return;
      const dirId = col.dataset.directionId;
      const original = btn.innerHTML;
      btn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" class="animate-spin-counterclockwise h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>';

      try {
        const data = await postJSON(API.refresh, { direction_id: dirId });
        const list = col.querySelector('.phone-list');
        list.innerHTML = '';
        (data.phones || []).forEach(p => list.appendChild(
          this.phoneItemDOM(typeof p === 'string' ? { number: p } : p)
        ));

        // Сразу после пересборки DOM переинициализируй Sortable
        this.initSortable(list);

        alert(`Номера успешно обновлены!\nДобавлено: ${data.added ?? '?'}, Удалено: ${data.removed ?? '?'}, Всего: ${data.total ?? '?'}`);
      } catch (err) {
        console.error(err);
        alert('Ошибка при обновлении номеров');
      } finally {
        btn.innerHTML = original;
      }
    }

    /* ───────────────────────── Modal CRUD */
    openCreateModal() {
      this.editingColumn = null;
      this.modalTitle.textContent = 'Новый столбец';
      this.titleInput.value = '';
      this.systemSelect.value = 'BX';
      this.selectedColor   = DEF_COLOR;
      this.pickr.setColor(this.selectedColor);
      this.toggleSaveBtn();
      this.overlay.classList.remove('hidden');
    }

    openEditModal(col) {
      if (!col) return;
      this.editingColumn = col;
      this.modalTitle.textContent = 'Редактировать столбец';
      this.titleInput.value = col.querySelector('h3').textContent;
      this.systemSelect.value = col.dataset.sourceSystem || 'BX';
      this.selectedColor   = col.dataset.color || DEF_COLOR;
      this.pickr.setColor(this.selectedColor);
      this.toggleSaveBtn();
      this.overlay.classList.remove('hidden');
    }

    closeModal() {
      this.overlay.classList.add('hidden');
      this.editingColumn = null;
    }

    toggleSaveBtn() {
      this.saveBtn.disabled = !(this.titleInput.value.trim() && this.selectedColor);
    }

    async handleSave() {
      const payload = { 
        title: this.titleInput.value.trim(), 
        color: this.selectedColor,
        source_system: this.systemSelect.value
      };
      if (!payload.title) return;
      try {
        if (this.editingColumn) {
          const data = await patchJSON(API.columns + this.editingColumn.dataset.directionId + '/', payload);
          this.applyColumnData(this.editingColumn, data);
          // Инициализируем все списки после редактирования столбца
          document.querySelectorAll('.phone-list').forEach(list => {
            this.initSortable(list);
          });
        } else {
          const data = await postJSON(API.columns, payload);
          const col = this.columnDOM(data);
          this.board.insertBefore(col, this.addBtn);

          // Инициализируем все списки после добавления столбца
          document.querySelectorAll('.phone-list').forEach(list => {
            this.initSortable(list);
          });

          col.scrollIntoView({ behavior: 'smooth', inline: 'nearest' });
        }
        this.closeModal();
      } catch (err) {
        console.error(err);
        
        // Improved error handling
        if (err.status === 400) {
          // Try to get the detailed error message from the response
          try {
            const errorText = await err.text();
            if (errorText.includes("not in bitrix")) {
              alert("Ошибка: Указанное направление не найдено в Bitrix. Пожалуйста, используйте только существующие направления из Bitrix.");
            } else {
              alert(`Ошибка: ${errorText}`);
            }
          } catch (textErr) {
            alert("Ошибка: Неверные данные. Пожалуйста, проверьте введенную информацию.");
          }
        } else {
          alert("Произошла ошибка при сохранении направления. Пожалуйста, попробуйте еще раз.");
        }
      }
    }

    applyColumnData(col, {title, color, source_system}) {
      col.querySelector('h3').textContent = title;
      col.querySelector('.column-header').style.background = color;
      col.dataset.color = color;
      col.dataset.sourceSystem = source_system;
      // Update system badge
      const badge = col.querySelector('.system-badge');
      if (badge) {
        badge.textContent = source_system;
        badge.title = source_system === 'BX' ? 'Bitrix' : 'Control-prosto';
        badge.className = `system-badge inline-flex items-center justify-center px-1.5 py-0.5 mr-1.5 text-[10px] font-bold rounded text-white ${source_system === 'BX' ? 'bg-blue-500' : 'bg-emerald-500'}`;
      }
    }

    /* ───────────────────────── DOM Builders */
    columnDOM({id, title, color, source_system = 'BX'}) {
      const wrap = document.createElement('div');
      wrap.className = 'kanban-column shrink-0 flex flex-col rounded-lg shadow-md dark:shadow-gray-900/70 overflow-hidden w-72 min-w-72 h-full border dark:border-gray-700';
      wrap.dataset.directionId = id;
      wrap.dataset.color       = color;
      wrap.dataset.sourceSystem = source_system;
      const badgeClass = source_system === 'BX' ? 'bg-blue-500' : 'bg-emerald-500';
      const badgeTitle = source_system === 'BX' ? 'Bitrix' : 'Control-prosto';
      wrap.innerHTML = `
        <div class="column-header flex items-center justify-between px-2 py-2" style="background:${color}">
          <div class="flex items-center min-w-0">
            <span class="system-badge inline-flex items-center justify-center px-1.5 py-0.5 mr-1.5 text-[10px] font-bold rounded ${badgeClass} text-white" title="${badgeTitle}">${source_system}</span>
            <h3 class="text-sm font-semibold text-white truncate">${title}</h3>
          </div>
          <div class="flex space-x-2 ml-2">
            ${this.iconBtn('edit',   this.svgEdit(),   'Редактировать')}
            ${this.iconBtn('delete', this.svgTrash(),  'Удалить')}
          </div>
        </div>
        <div class="bg-gray-100 dark:bg-gray-800 p-2 flex-1 flex flex-col overflow-hidden">
          <div class="flex items-center justify-between mb-2 px-1">
            <label class="flex items-center">
              <input type="checkbox" class="select-all-checkbox h-3 w-3 rounded text-purple-600 focus:ring-purple-500 mr-2">
              <span class="text-xs text-gray-600 dark:text-gray-400">Выбрать все</span>
            </label>
            <div class="bulk-actions hidden flex flex-row items-center gap-1 flex-nowrap">
              <button class="bulk-edit-rotation-date flex items-center rounded-full bg-blue-500 text-white text-[9px] font-semibold px-1 py-0.5 hover:bg-blue-600" title="Установить дату для выбранных">дата</button>
              <button class="bulk-edit-calls-limit flex items-center rounded-full bg-red-500 text-white text-[9px] font-semibold px-1 py-0.5 hover:bg-red-600" title="Установить лимит для выбранных">лимит</button>
            </div>
          </div>
          <div id="direction-${id}-list" class="phone-list space-y-1.5 flex-1 min-h-0 overflow-y-auto custom-scrollbar text-xs leading-tight break-words"></div>
        </div>`;
      return wrap;
    }

    phoneItemDOM({number, calls_limit=null, rotation_disable_date=null, calls_now=null}) {
      const div = document.createElement('div');
      div.className = 'phone-item bg-white dark:bg-gray-700 p-[6px] rounded shadow-sm dark:shadow-black/30 cursor-move truncate text-gray-900 dark:text-gray-100 flex items-center justify-between flex-nowrap group';
      div.dataset.phoneNumber = number;
      if (rotation_disable_date) div.dataset.rotationDate = rotation_disable_date;
      div.innerHTML = `
        <div class="flex items-center flex-1 min-w-0">
          <input type="checkbox" class="phone-checkbox h-3 w-3 rounded text-purple-600 focus:ring-purple-500 mr-2">
          <span class="phone-text truncate">${number}</span>
        </div>
        <div class="flex flex-row items-center gap-1 whitespace-nowrap flex-none">
          <button data-action="edit-rotation-date" title="Дата выключения из ротации" class="rotation-date-icon flex items-center rounded-full bg-blue-500 text-white text-[9px] font-semibold px-1 py-0.5 hover:bg-blue-600">
            <span class="rotation-date-text">${this.formatDateDMY(rotation_disable_date)}</span>
          </button>
          <button data-action="edit-calls-limit" title="Изменить лимит звонков" class="calls-limit ml-1 flex items-center rounded-full bg-red-500 text-white text-[9px] font-semibold px-1 py-0.5 hover:bg-red-600">${calls_limit ?? '∞'}</button>
        </div>`;
      return div;
    }

    // Helper: format date as d.m.Y
    formatDateDMY(value) {
      if (!value) return '—';
      // If already in d.m.Y format
      if (typeof value === 'string' && /^\d{2}\.\d{2}\.\d{4}$/.test(value)) return value;
      try {
        // Handle YYYY-MM-DD or ISO strings
        const [y, m, d] = typeof value === 'string' && /\d{4}-\d{2}-\d{2}/.test(value)
          ? value.substring(0, 10).split('-').map(Number)
          : (() => { const dt = new Date(value); return [dt.getFullYear(), dt.getMonth()+1, dt.getDate()]; })();
        if (!y || !m || !d) return '—';
        const dd = String(d).padStart(2, '0');
        const mm = String(m).padStart(2, '0');
        return `${dd}.${mm}.${y}`;
      } catch { return '—'; }
    }

    iconBtn(action, svg, title) {
      return `<button data-action="${action}" class="p-1 hover:bg-white/20 rounded text-white" title="${title}">${svg}</button>`;
    }

    svgEdit()   { return '<svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5h6m2 2l-7 7-3 3-4 1 1-4 3-3 7-7z"/></svg>'; }
    svgTrash()  { return '<svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3m4 0H5"/></svg>'; }
    svgRefresh(){ return '<svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/></svg>'; }

    /* ───────────────────────── Phone select & bulk ui */
    initSelectHandlers() {
      /* select-all */
      document.addEventListener('change', e => {
        if (e.target.classList.contains('select-all-checkbox')) this.handleSelectAll(e.target);
        if (e.target.classList.contains('phone-checkbox'))     this.handlePhoneCheckbox(e.target);
      });
    }

    handleSelectAll(chk) {
      const col = chk.closest('.kanban-column');
      const boxes = [...col.querySelectorAll('.phone-checkbox')];
      boxes.forEach(cb => {
        cb.checked = chk.checked;
        this.setPhoneSelection(cb.closest('.phone-item'), chk.checked);
      });
      this.updateBulkVisibility(col);
    }

    handlePhoneCheckbox(cb) {
      const item = cb.closest('.phone-item');
      this.setPhoneSelection(item, cb.checked);
      const col = cb.closest('.kanban-column');
      this.updateSelectAllState(col);
      this.updateBulkVisibility(col);
    }

    setPhoneSelection(item, isSel) {
      if (!item) return;
      item.classList.toggle(CLS_SELECTED, isSel);
      const list = item.closest('.phone-list');
      const s = Sortable.get(list);
      if (s?.options.multiDrag && window.Sortable?.utils) {
        (isSel ? Sortable.utils.select : Sortable.utils.deselect)(item);
      }
    }

    updateSelectAllState(col) {
      const all = col.querySelector('.select-all-checkbox');
      const phones = [...col.querySelectorAll('.phone-checkbox')];
      const allChecked = phones.every(cb => cb.checked);
      all.indeterminate = !allChecked && phones.some(cb => cb.checked);
      all.checked       = allChecked;
    }

    updateBulkVisibility(col) {
      const any = [...col.querySelectorAll('.phone-checkbox')].some(cb => cb.checked);
      col.querySelector('.bulk-actions')?.classList.toggle('hidden', !any);
    }

    /* ───────────────────────── Calls limit modal */
    initCallsLimitModal() {
      // Individual number modal
      document.getElementById('unlimited-calls')?.addEventListener('change', e => {
        document.getElementById('limit-input-container').style.display = e.target.checked ? 'none' : 'flex';
      });
      document.getElementById('cancel-calls-limit')?.addEventListener('click', () => this.hideCallsLimitModal());
      document.getElementById('save-calls-limit')?.addEventListener('click', () => this.saveCallsLimit('calls-limit-modal'));

      // Bulk modal
      document.getElementById('bulk-unlimited-calls')?.addEventListener('change', e => {
        document.getElementById('bulk-limit-input-container').style.display = e.target.checked ? 'none' : 'flex';
      });
      document.getElementById('cancel-bulk-calls-limit')?.addEventListener('click', () => this.hideBulkCallsLimitModal());
      document.getElementById('save-bulk-calls-limit')?.addEventListener('click', () => this.saveCallsLimit('bulk-calls-limit-modal'));
    }

    initBulkCallsLimitHandlers() {
      document.addEventListener('click', e => {
        if (e.target.closest('.bulk-edit-calls-limit')) {
          const col = e.target.closest('.kanban-column');
          this.showBulkCallsLimitModal(col);
        }
      });
    }

    showCallsLimitModal(e) {
      const item = e.target.closest('.phone-item');
      if (!item) return;
      const number = item.dataset.phoneNumber;
      const currentLimit = item.querySelector('.calls-limit')?.textContent.trim();

      // Update the phone number display in the modal
      const phoneDisplay = document.querySelector('#calls-limit-modal .phone-number-display');
      if (phoneDisplay) phoneDisplay.textContent = number;

      // Prepare individual modal
      this.prepareCallsModal('calls-limit-modal', [number], currentLimit);

      // Show the modal overlay
      document.getElementById('calls-limit-overlay')?.classList.remove('hidden');
    }

    showBulkCallsLimitModal(col) {
      const nums = [...col.querySelectorAll('.phone-checkbox:checked')].map(cb => cb.closest('.phone-item').dataset.phoneNumber);
      if (nums.length === 0) return alert('Выберите номера');

      // Update the selected count in the bulk modal
      const countSpan = document.querySelector('#bulk-calls-limit-modal .selected-count');
      if (countSpan) countSpan.textContent = nums.length;

      // Prepare bulk modal
      this.prepareCallsModal('bulk-calls-limit-modal', nums, null);

      // Show the modal overlay
      document.getElementById('bulk-calls-limit-overlay')?.classList.remove('hidden');
    }

    prepareCallsModal(modalId, numbers, currentLimit) {
      const modal = document.getElementById(modalId);
      if (!modal) return;

      modal.dataset.phoneNumbers = JSON.stringify(numbers);

      // Determine if this is the bulk modal
      const isBulk = modalId === 'bulk-calls-limit-modal';

      // Get the appropriate elements based on prefix
      const prefix = isBulk ? 'bulk-' : '';
      const unlimited = modal.querySelector(`#${prefix}unlimited-calls`);
      const input = modal.querySelector(`#${prefix}calls-limit-input`);
      const container = modal.querySelector(`#${prefix}limit-input-container`);

      if (!unlimited || !container || !input) return;

      if (currentLimit === '∞' || currentLimit === null) {
        unlimited.checked = true;
        container.style.display = 'none';
        input.value = '';
      } else if (currentLimit) {
        unlimited.checked = false;
        container.style.display = 'flex';
        input.value = currentLimit;
      } else {
        unlimited.checked = false;
        container.style.display = 'flex';
        input.value = '';
      }
    }

    hideCallsLimitModal() {
      document.getElementById('calls-limit-overlay')?.classList.add('hidden');
    }

    hideBulkCallsLimitModal() {
      document.getElementById('bulk-calls-limit-overlay')?.classList.add('hidden');
    }

    async saveCallsLimit(modalId) {
      const modal = document.getElementById(modalId);
      if (!modal) return;

      const numbers = JSON.parse(modal.dataset.phoneNumbers || '[]');

      const isBulk = modalId === 'bulk-calls-limit-modal';
      const prefix = isBulk ? 'bulk-' : '';
      const unlimited = modal.querySelector(`#${prefix}unlimited-calls`).checked;
      const input = modal.querySelector(`#${prefix}calls-limit-input`);

      const limitVal = unlimited ? null : parseInt(input.value, 10);

      if (!unlimited && (isNaN(limitVal) || limitVal < 0)) {
        return alert('Введите корректный лимит');
      }

      const payload = { phones: numbers, limit: limitVal };

      try {
        const response = await fetch(API.callsLimit, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': csrf()
          },
          body: JSON.stringify(payload)
        });

        const responseData = await response.json();

        if ((response.status >= 200 && response.status < 300) || response.status === 207) {
          numbers.forEach(num => {
            document.querySelectorAll(`.phone-item[data-phone-number="${num}"]`).forEach(item => {
              item.querySelector('.calls-limit').textContent = limitVal ?? '∞';
            });
          });

          if (isBulk) {
            this.hideBulkCallsLimitModal();
          } else {
            this.hideCallsLimitModal();
          }

          // Важное добавление – переинициализация Sortable после обновления лимитов
          const affectedColumns = new Set();
          numbers.forEach(num => {
            document.querySelectorAll(`.phone-item[data-phone-number="${num}"]`).forEach(item => {
              const col = item.closest('.kanban-column');
              if (col) affectedColumns.add(col);
            });
          });

          affectedColumns.forEach(col => {
            const list = col.querySelector('.phone-list');
            if (list) this.initSortable(list);
          });

        } else {
          const errorMessage = responseData.error || 'Не удалось изменить лимиты';
          console.error('API error:', errorMessage);
          alert(errorMessage);
        }
      } catch(err) {
        console.error('API error:', err);
        alert('Не удалось изменить лимиты: ошибка сети или сервера');
      }
    }
    
    /* ───────────────────────── Rotation disable date modal */
    initRotationDateModal() {
      // Individual modal
      document.getElementById('no-rotation-date')?.addEventListener('change', e => {
        const c = document.getElementById('rotation-date-input-container');
        if (c) c.style.display = e.target.checked ? 'none' : 'flex';
      });
      document.getElementById('cancel-rotation-date')?.addEventListener('click', () => this.hideRotationDateModal());
      document.getElementById('save-rotation-date')?.addEventListener('click', () => this.saveRotationDate('rotation-date-modal'));

      // Bulk modal
      document.getElementById('bulk-no-rotation-date')?.addEventListener('change', e => {
        const c = document.getElementById('bulk-rotation-date-input-container');
        if (c) c.style.display = e.target.checked ? 'none' : 'flex';
      });
      document.getElementById('cancel-bulk-rotation-date')?.addEventListener('click', () => this.hideBulkRotationDateModal());
      document.getElementById('save-bulk-rotation-date')?.addEventListener('click', () => this.saveRotationDate('bulk-rotation-date-modal'));
    }

    initBulkRotationDateHandlers() {
      document.addEventListener('click', e => {
        if (e.target.closest('.bulk-edit-rotation-date')) {
          const col = e.target.closest('.kanban-column');
          this.showBulkRotationDateModal(col);
        }
      });
    }

    showRotationDateModal(e) {
      const item = e.target.closest('.phone-item');
      if (!item) return;
      const number = item.dataset.phoneNumber;
      const currentDate = item.querySelector('.rotation-date-text')?.textContent.trim();

      const phoneDisplay = document.querySelector('#rotation-date-modal .phone-number-display');
      if (phoneDisplay) phoneDisplay.textContent = number;

      this.prepareRotationModal('rotation-date-modal', [number], currentDate);
      document.getElementById('rotation-date-overlay')?.classList.remove('hidden');
    }

    showBulkRotationDateModal(col) {
      const nums = [...col.querySelectorAll('.phone-checkbox:checked')].map(cb => cb.closest('.phone-item').dataset.phoneNumber);
      if (nums.length === 0) return alert('Выберите номера');

      const countSpan = document.querySelector('#bulk-rotation-date-modal .selected-count');
      if (countSpan) countSpan.textContent = nums.length;

      this.prepareRotationModal('bulk-rotation-date-modal', nums, null);
      document.getElementById('bulk-rotation-date-overlay')?.classList.remove('hidden');
    }

    prepareRotationModal(modalId, numbers, currentDate) {
      const modal = document.getElementById(modalId);
      if (!modal) return;
      modal.dataset.phoneNumbers = JSON.stringify(numbers);

      const isBulk = modalId === 'bulk-rotation-date-modal';
      const prefix = isBulk ? 'bulk-' : '';
      const noneChk = modal.querySelector(`#${prefix}no-rotation-date`);
      const input = modal.querySelector(`#${prefix}rotation-date-input`);
      const container = modal.querySelector(`#${prefix}rotation-date-input-container`);
      if (!noneChk || !input || !container) return;

      const isUnset = !currentDate || currentDate === '—';
      noneChk.checked = isUnset;
      container.style.display = isUnset ? 'none' : 'flex';
      if (isUnset) {
        input.value = '';
      } else {
        // If UI shows d.m.Y, convert to YYYY-MM-DD for <input type="date">
        if (/^\d{2}\.\d{2}\.\d{4}$/.test(currentDate)) {
          const [dd, mm, yyyy] = currentDate.split('.');
          input.value = `${yyyy}-${mm}-${dd}`;
        } else {
          input.value = currentDate; // assume already YYYY-MM-DD
        }
      }
    }

    hideRotationDateModal() {
      document.getElementById('rotation-date-overlay')?.classList.add('hidden');
    }

    hideBulkRotationDateModal() {
      document.getElementById('bulk-rotation-date-overlay')?.classList.add('hidden');
    }

    async saveRotationDate(modalId) {
      const modal = document.getElementById(modalId);
      if (!modal) return;
      const numbers = JSON.parse(modal.dataset.phoneNumbers || '[]');
      const isBulk = modalId === 'bulk-rotation-date-modal';
      const prefix = isBulk ? 'bulk-' : '';
      const noneChk = modal.querySelector(`#${prefix}no-rotation-date`).checked;
      const input = modal.querySelector(`#${prefix}rotation-date-input`);
      const dateVal = noneChk ? null : (input.value || null);

      if (!noneChk && dateVal && !/^\d{4}-\d{2}-\d{2}$/.test(dateVal)) {
        return alert('Введите корректную дату в формате YYYY-MM-DD');
      }

      const payload = { phones: numbers, date: dateVal };
      try {
        const resp = await fetch(API.rotationDate, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-CSRFToken': csrf() },
          body: JSON.stringify(payload)
        });
        const data = await resp.json();
        if ((resp.status >= 200 && resp.status < 300) || resp.status === 207) {
          numbers.forEach(num => {
            document.querySelectorAll(`.phone-item[data-phone-number="${num}"]`).forEach(item => {
              const el = item.querySelector('.rotation-date-text');
              if (el) el.textContent = this.formatDateDMY(dateVal);
              if (dateVal) item.dataset.rotationDate = dateVal; else item.dataset.rotationDate = '';
            });
          });
          if (isBulk) this.hideBulkRotationDateModal(); else this.hideRotationDateModal();

          const affected = new Set();
          numbers.forEach(num => {
            document.querySelectorAll(`.phone-item[data-phone-number="${num}"]`).forEach(item => {
              const col = item.closest('.kanban-column');
              if (col) affected.add(col);
            });
          });
          affected.forEach(col => {
            const list = col.querySelector('.phone-list');
            if (list) this.initSortable(list);
          });
        } else {
          console.error('API error:', data.error || data);
          alert(data.error || 'Не удалось сохранить дату');
        }
      } catch (err) {
        console.error('API error:', err);
        alert('Не удалось сохранить дату: ошибка сети или сервера');
      }
    }
  }

  /* ─────────────────────────── IIFE Entry */
  document.addEventListener('DOMContentLoaded', () => new Kanban());
})();
